import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { contacts } from '@repletoryreach/sdk-api';
import { Button, Text } from '@repletoryreach/sdk-ui';
import { Popover } from '@repletoryreach/sdk-ui/overlays';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { useT } from '../../../i18n/I18nProvider';
import { TagPicker, TAGS_KEY, useTagVocabulary } from './TagPicker';

/**
 * What the row checkboxes are for.
 *
 * Tags are how this product groups contacts, so assembling a group means
 * tagging a set of people: the selection has to be able to gain or lose a tag
 * in one go, and then be sent to. Doing it one contact at a time meant forty
 * round trips and a race with anyone else editing them.
 */
export function BulkBar({
  selected,
  onClear,
  onMessage
}: {
  selected: string[];
  onClear: () => void;
  /**
   * Message the selection directly. Until compose existed the only route from
   * "these people" to "message them" ran through tagging them and sending a
   * broadcast to the tag, which is the right shape for two hundred people and
   * the wrong one for three.
   */
  onMessage?: (ids: string[]) => void;
}) {
  const t = useT();
  const qc = useQueryClient();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [tag, setTag] = useState<string[]>([]);
  const { tags: vocabulary } = useTagVocabulary();

  const apply = useMutation({
    mutationFn: ({ name, action }: { name: string; action: 'add' | 'remove' }) =>
      contacts.bulkTag({ tag: name, action, contact_ids: selected }),
    onSuccess: (res) => {
      const { tag: name, action, changed } = res.data;
      notifySuccess(
        action === 'add'
          ? t('contacts.bulk.tagged', { tag: name, count: changed })
          : t('contacts.bulk.untagged', { tag: name, count: changed }),
        t('contacts.title')
      );
      qc.invalidateQueries({ queryKey: ['contacts'] });
      qc.invalidateQueries({ queryKey: TAGS_KEY });
      setTag([]);
      setOpen(false);
    },
    onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('contacts.title'))
  });

  if (selected.length === 0) return null;

  const chosen = tag[0] ?? '';

  return (
    <div className="rr-contacts__bulk" role="region" aria-label={t('contacts.bulk.label')}>
      <Text role="bodySmall" fw={600}>{t('contacts.bulk.count', { count: selected.length })}</Text>

      <Popover opened={open} onChange={setOpen} position="bottom-start" width={280}>
        <Popover.Target>
          <Button size="xs" variant="default">{t('contacts.bulk.tag')}</Button>
        </Popover.Target>
        <Popover.Dropdown>
          <div className="rr-contacts__bulk-panel">
            <TagPicker
              id="bulk-tag"
              label={t('contacts.bulk.tagLabel')}
              description={t('contacts.bulk.tagHint')}
              // One tag at a time: "add these four tags and remove that one"
              // is a different, rarer job, and conflating them makes the
              // common case ambiguous.
              value={tag.slice(0, 1)}
              onChange={(next) => setTag(next.slice(-1))}
            />
            <div className="rr-contacts__bulk-actions">
              <Button
                size="xs"
                disabled={!chosen}
                loading={apply.isPending && apply.variables?.action === 'add'}
                onClick={() => apply.mutate({ name: chosen, action: 'add' })}
              >
                {t('contacts.bulk.add')}
              </Button>
              <Button
                size="xs"
                variant="default"
                disabled={!chosen}
                loading={apply.isPending && apply.variables?.action === 'remove'}
                onClick={() => apply.mutate({ name: chosen, action: 'remove' })}
              >
                {t('contacts.bulk.remove')}
              </Button>
            </div>
          </div>
        </Popover.Dropdown>
      </Popover>

      {/* A few people: message them where they are. */}
      {onMessage && (
        <Button size="xs" variant="default" onClick={() => onMessage(selected)}>
          {t('contacts.bulk.message')}
        </Button>
      )}

      {/* Many people: tag them and send to the tag, which is what a broadcast
          is for and what compose declines to imitate. */}
      <Button
        size="xs"
        variant="default"
        disabled={vocabulary.length === 0}
        onClick={() => nav('/broadcasts')}
      >
        {t('contacts.bulk.broadcast')}
      </Button>

      <Button size="xs" variant="subtle" onClick={onClear}>{t('contacts.bulk.clear')}</Button>
    </div>
  );
}
